import type { ReactElement } from 'react';
import { isExternalAiStatusMeter, isLocalRuntimeMeter, type AiCapacityMeter, type ExternalAiStatusMeter, type LocalRuntimeMeterState } from '../lib/ai-usage-view';
import '../ai-usage-gauge.css';

export type AiUsageGaugeSize = 'compact' | 'detail';

export interface AiUsageGaugeProps {
  readonly meter: AiCapacityMeter;
  readonly size?: AiUsageGaugeSize;
}

function percentText(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}%` : `${rounded.toFixed(1)}%`;
}

function remainingPercent(value: number): number {
  return Math.max(0, Math.min(100, 100 - value));
}

function quotaLabel(meter: AiCapacityMeter): string | null {
  if (meter.quotaSignal === 'exhausted') return 'Exhausted';
  if (meter.quotaSignal === 'rate_limited') return 'Rate limited';
  if (meter.quotaSignal === 'signed_out') return 'Signed out';
  if (meter.quotaSignal === 'near_limit') return 'Near limit';
  if (meter.quotaSignal === 'unknown') return 'Unknown';
  if (meter.band === 'hard_exceeded') return 'Hard limit';
  if (meter.band === 'reserve_breached') return 'Reserve';
  if (meter.band === 'soft_exceeded' || meter.band === 'warn') return 'Getting high';
  return null;
}

function headline(meter: AiCapacityMeter): string {
  return meter.model ?? meter.title;
}

function contextLine(meter: AiCapacityMeter): string | null {
  const parts = [meter.provider, meter.runtime].filter((part): part is string => Boolean(part));
  const unique = [...new Set(parts.filter((part) => part !== headline(meter)))];
  return unique.length === 0 ? null : unique.join(' · ');
}

const LOCAL_STATE_LABELS: Readonly<Record<LocalRuntimeMeterState, string>> = {
  available: 'Available',
  busy: 'Busy',
  memory_pressure: 'Memory pressure',
  offline: 'Offline',
  unavailable: 'Unavailable',
  unknown: 'Unknown',
};

function localPercentLabel(value: number | null, label: string): string | null {
  if (value === null || !Number.isFinite(value)) return null;
  return `${Math.round(value * 10) / 10}% ${label}`;
}

function LocalRuntimeGauge({
  meter,
  size,
}: {
  readonly meter: Extract<AiCapacityMeter, never> | AiCapacityMeter;
  readonly size: AiUsageGaugeSize;
}): ReactElement {
  if (!isLocalRuntimeMeter(meter)) throw new Error('local runtime meter required');
  const local = meter.localRuntime;
  const state = LOCAL_STATE_LABELS[local.state];
  const loaded = local.loadedModelsKnown
    ? (local.loadedModels.length > 0 ? local.loadedModels.map((model) => model.name).join(' · ') : 'No loaded model')
    : 'Loaded model unknown';
  const resources = [
    localPercentLabel(local.memoryFreePercent, 'memory free'),
    localPercentLabel(local.videoMemoryFreePercent, 'video memory free'),
  ].filter((value): value is string => value !== null);

  return (
    <article
      className={`ai-usage-gauge ai-usage-gauge-${size} local-runtime-meter tone-${meter.tone}`}
      title={`${meter.title} · ${local.nodeName} · Local / Unlimited · ${state}`}
      data-usage-state="local-runtime"
      data-local-state={local.state}
      data-meter-kind="local-runtime"
    >
      <header className="ai-usage-gauge-head">
        <span className="stack tight">
          <strong>{meter.title}</strong>
          <small className="muted">{local.nodeName}</small>
        </span>
        <span className="row ai-usage-gauge-badges">
          <span className="ai-measure-tag ai-measure-local">Local / Unlimited</span>
          <span className="ai-quota-tag">{state}</span>
        </span>
      </header>
      <div className="local-runtime-meter-body" aria-label={`${meter.title} on ${local.nodeName}`}>
        <strong>{loaded}</strong>
        <span className="muted">{resources.length > 0 ? resources.join(' · ') : 'Machine resource reading unknown'}</span>
      </div>
      <div className="ai-usage-gauge-foot">
        <span>{local.runtimeAvailable === true ? 'Runtime available' : local.runtimeAvailable === false ? 'Runtime unavailable' : 'Runtime availability unknown'}</span>
        <span className="muted">{local.serviceState === 'reachable' ? 'Service reachable' : local.serviceState === 'unreachable' ? 'Service unavailable' : 'Service unknown'}</span>
      </div>
    </article>
  );
}


function externalCapabilityLabel(meter: ExternalAiStatusMeter): string {
  if (meter.externalAi.entityKind === 'executable_runtime') {
    if (meter.externalAi.connectionState === 'available') return 'Executable ready';
    if (meter.externalAi.connectionState === 'unavailable') return 'Runtime unavailable';
    return 'Runtime Unknown';
  }
  if (meter.externalAi.effectiveCapability === 'read_only') return 'Read-only';
  if (meter.externalAi.effectiveCapability === 'write_capable') return 'Write-capable';
  if (meter.externalAi.effectiveCapability === 'unavailable') return 'Unavailable';
  return 'Capability Unknown';
}

function ExternalAiStatusGauge({
  meter,
  size,
}: {
  readonly meter: ExternalAiStatusMeter;
  readonly size: AiUsageGaugeSize;
}): ReactElement {
  const percent = meter.usedPercent !== null && Number.isFinite(meter.usedPercent)
    ? Math.max(0, Math.min(100, meter.usedPercent))
    : null;
  const capability = externalCapabilityLabel(meter);
  const connection = meter.externalAi.connectionState === 'connected'
    ? 'Connected'
    : meter.externalAi.connectionState === 'available'
      ? 'Available'
      : meter.externalAi.connectionState === 'unavailable'
        ? 'Unavailable'
        : 'Status Unknown';
  return (
    <article
      className={'ai-usage-gauge ai-usage-gauge-' + size + ' external-ai-status-meter tone-' + meter.tone}
      title={meter.title + ' · ' + capability + ' · ' + (percent === null ? 'capacity Unknown' : percentText(percent) + ' used')}
      data-usage-state="external-status"
      data-meter-kind="external-ai-status"
      data-entity-key={meter.externalAi.entityKey}
    >
      <header className="ai-usage-gauge-head">
        <span className="stack tight">
          <strong>{meter.title}</strong>
          <small className="muted">{meter.externalAi.entityKind.replaceAll('_', ' ')}</small>
        </span>
        <span className="row ai-usage-gauge-badges">
          <span className="ai-measure-tag ai-measure-status">Status</span>
          <span className="ai-quota-tag">{connection}</span>
        </span>
      </header>
      <div className="external-ai-status-body">
        <strong>{capability}</strong>
        <span>{percent === null ? 'Capacity Unknown' : percentText(percent) + ' used'}</span>
        {meter.resetsLabel !== null && <small className="muted">{meter.resetsLabel}</small>}
      </div>
      <div className="ai-usage-gauge-foot">
        <span>{meter.externalAi.authState === 'signed_in' ? 'Signed in' : meter.externalAi.authState === 'signed_out' ? 'Signed out' : 'Auth Unknown'}</span>
        <span className="muted">{meter.externalAi.lastVerifiedAt === null ? 'Not verified' : 'Verified'}</span>
      </div>
    </article>
  );
}

export function AiUsageGauge({ meter, size = 'compact' }: AiUsageGaugeProps): ReactElement {
  if (isLocalRuntimeMeter(meter)) return <LocalRuntimeGauge meter={meter} size={size} />;
  if (isExternalAiStatusMeter(meter)) return <ExternalAiStatusGauge meter={meter} size={size} />;
  const knownPercent = meter.usedPercent !== null && Number.isFinite(meter.usedPercent);
  const used = knownPercent ? Math.max(0, Math.min(100, meter.usedPercent!)) : null;
  const remaining = used === null ? null : remainingPercent(used);
  const state = quotaLabel(meter);

  return (
    <article
      className={`ai-usage-gauge ai-usage-gauge-${size} tone-${meter.tone} ${used === null ? 'is-unknown' : ''}`}
      title={`${headline(meter)} · ${contextLine(meter) ?? meter.provider} · ${meter.measurementPhrase}`}
      data-usage-state={meter.band}
      data-quota-state={meter.quotaSignal}
    >
      <header className="ai-usage-gauge-head">
        <span className="stack tight">
          <strong title={headline(meter)}>{headline(meter)}</strong>
          {contextLine(meter) !== null && <small className="muted">{contextLine(meter)}</small>}
        </span>
        <span className="row ai-usage-gauge-badges">
          <span className={`ai-measure-tag ai-measure-${meter.measurementLabel ?? 'unknown'}`}>{meter.measurementLabel ?? 'Unknown'}</span>
          {state !== null && <span className="ai-quota-tag" title={state}>{size === 'compact' && state === 'Getting high' ? 'High' : state}</span>}
        </span>
      </header>

      <div
        className="ai-usage-dial"
        role="meter"
        aria-label={`${meter.title} usage`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={used === null ? undefined : used}
        aria-valuetext={used === null ? 'Unknown usage percentage' : `${percentText(used)} used`}
      >
        <svg viewBox="0 0 120 66" aria-hidden="true">
          <path className="ai-usage-arc-track" pathLength="100" d="M 10 58 A 50 50 0 0 1 110 58" />
          {used !== null && (
            <path
              className="ai-usage-arc-value"
              pathLength="100"
              d="M 10 58 A 50 50 0 0 1 110 58"
              style={{ strokeDasharray: `${used} ${100 - used}` }}
            />
          )}
        </svg>
        <div className="ai-usage-dial-value">
          {used === null ? (
            <strong>Unknown</strong>
          ) : (
            <>
              <strong>{percentText(used)}</strong>
              <span>used</span>
            </>
          )}
        </div>
      </div>

      <div className="ai-usage-gauge-foot">
        {remaining === null ? (
          <span className="muted">Remaining unknown</span>
        ) : (
          <span><strong>{percentText(remaining)}</strong> remaining</span>
        )}
        {meter.resetsLabel !== null && <span className="muted">{meter.resetsLabel}</span>}
      </div>
    </article>
  );
}
